const appDomain = (import.meta.env.VITE_LOGIN_URL ?? '').replace('/login', '');

// Matches the UTM scheme Invoice Central's static FinanceIQ promo already uses (see
// FinanceIqFallbackAlert) — a distinct utm_series identifies spotlight-driven clicks separately
// from that generic promo's clicks in analytics.
const FIQ_UTM_PARAMS = {
  utm_partner: '',
  utm_lang: 'en_US',
  utm_source: 'c2fo',
  utm_medium: 'in_app',
  utm_region: 'amer',
  utm_lifecycle: 'cross',
  utm_audience: 'na',
  utm_campaign: 'financeiq',
  utm_series: 'invoicecentral-kpispotlight',
  utm_country: 'us',
  utm_content: '',
  utm_term: '',
} as const;

/**
 * Builds the FinanceIQ deep link for one buyer's spotlight KPI.
 *
 * `ctaDeepLink` is a bare "{tab}?{query}" fragment from the API (e.g. "/invoices?status=PAST_DUE",
 * "/deductions?startDate=...&endDate=...") — the same shape invoice-hub-ui's own Focus Center
 * notifications use for these same four KPI signals, since both are computed by the shared
 * KpiEvaluatorService. FinanceIQ's buyer-scoped routes live under
 * "/insights/{makerOrganizationUuid}/{tab}", not at that bare tab path, so this mirrors
 * invoice-hub-ui's own link builder (apps/invoice-hub/ui/src/utils/buildNotificationCustomerUrl.ts)
 * rather than naively concatenating the raw path onto the FinanceIQ domain.
 *
 * `currency` is added here rather than by the API: the spotlight is scoped to one currency, but the
 * shared KpiEvaluatorService authors `ctaDeepLink` for currency-agnostic Focus Center notifications
 * too, so only this caller knows the scope. Both insights tabs read a `currency` search param as a
 * row filter (useUnifiedInvoiceState / useUnifiedDeductionState); without it the user lands on an
 * unfiltered all-currency view whose totals do not reconcile with the banner they clicked.
 */
export const createKpiSpotlightUrl = (makerOrganizationUuid: string, ctaDeepLink: string, currency: string): string => {
  const [pathPart, queryPart] = ctaDeepLink.split('?');
  const tab = pathPart.replace(/^\//u, '');
  const params = new URLSearchParams(queryPart);
  params.set('currency', currency);
  Object.entries(FIQ_UTM_PARAMS).forEach(([key, value]) => params.set(key, value));
  return `${appDomain}/financeiq/insights/${makerOrganizationUuid}/${tab}?${params.toString()}`;
};
